import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { Clock, Activity, FileText } from 'lucide-react';
import styles from './PauseAnalysis.module.css';
import InfoModal from '../InfoModal/InfoModal';

const PauseAnalysis = ({ environment, segments }) => {
  const getPauseThresholds = (env) => {
    const thresholds = {
      'Corporate Presentations': {
        word: 0.5,
        sentence: 1.5,
        emphasis: 2.5,
        color: '#3b82f6'
      },
      'Social Media/YouTube Content': {
        word: 0.3,
        sentence: 1.2,
        emphasis: 2.0,
        color: '#10b981'
      },
      'Academic/Technical': {
        word: 0.5,
        sentence: 2.0,
        emphasis: 2.5,
        color: '#8b5cf6'
      },
      'Motivational Speaking': {
        word: 0.4,
        sentence: 1.5,
        emphasis: 3.0,
        color: '#f59e0b'
      },
      'TV/Broadcasting': {
        word: 0.3,
        sentence: 1.2,
        emphasis: 2.0,
        color: '#ef4444'
      },
      'Sales/Client Presentations': {
        word: 0.4,
        sentence: 1.5,
        emphasis: 2.5,
        color: '#6366f1'
      }
    };

    return thresholds[environment] || {
      word: 0.5,
      sentence: 1.5,
      emphasis: 2.5,
      color: '#3b82f6'
    };
  };

  const { timelineData, totalDuration, maxPause, wordCount } = useMemo(() => {
    if (!segments?.length) return { timelineData: [], totalDuration: 0, maxPause: 0, wordCount: 0 };

    const thresholds = getPauseThresholds(environment);
    let data = [];
    let maxPause = 0;
    let actualWordCount = 0;
    const startTime = segments[0].timestamp;

    const isPunctuation = (content) => /^[.,!?;:]$/.test(content.trim());
    actualWordCount = segments.filter(segment => !isPunctuation(segment.content)).length;

    if (!isPunctuation(segments[0].content)) {
      data.push({
        time: 0,
        pause: 0,
        word: segments[0].content,
        tooltipContent: segments[0].content,
        showDot: false
      });
    }

    for (let i = 1; i < segments.length; i++) {
      const currentSegment = segments[i];
      const prevSegment = segments[i - 1];
      const pause = (currentSegment.timestamp - prevSegment.timestamp) / 1000;
      const timePoint = (currentSegment.timestamp - startTime) / 1000;

      maxPause = Math.max(maxPause, pause);

      if (!isPunctuation(currentSegment.content)) {
        const showDot = pause >= 1.0 || (i > 1 && i < segments.length - 1 && (
          (data[data.length - 1]?.pause < pause && segments[i + 1]?.pause < pause) ||
          (data[data.length - 1]?.pause > pause && segments[i + 1]?.pause > pause)
        ));

        data.push({
          time: timePoint,
          pause: pause,
          word: currentSegment.content,
          tooltipContent: `${currentSegment.content} (${pause.toFixed(1)}s pause)`,
          showDot,
          pauseCategory: pause < thresholds.word ? 'Normal' :
            pause < thresholds.sentence ? 'Moderate' :
              pause < thresholds.emphasis ? 'Significant' : 'Dramatic'
        });
      }
    }

    return {
      timelineData: data,
      totalDuration: (segments[segments.length - 1].timestamp - startTime) / 1000,
      maxPause,
      wordCount: actualWordCount
    };
    // eslint-disable-next-line
  }, [segments, environment]);

  if (!segments?.length) return null;

  const thresholds = getPauseThresholds(environment);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;

    const pauseData = payload[0].payload;
    return (
      <div className={styles.customTooltip}>
        <p className={styles.tooltipText}>{pauseData.tooltipContent}</p>
        <p className={styles.tooltipCategory}>
          Pause Category: {pauseData.pauseCategory}
        </p>
      </div>
    );
  };

  const MetricCard = ({ icon, label, value, unit = '' }) => (
    <div className={styles.metricCard}>
      {icon}
      <div className={styles.metricContent}>
        <span className={styles.metricLabel}>{label}</span>
        <span className={styles.metricValue}>
          {value}{unit}
        </span>
      </div>
    </div>
  );

  return (
    <div className={styles.analysisSection}>
      <div className={styles.titleContainer}>
        <InfoModal type="pause" environment={environment} />
      </div>
      <div className={styles.metricsGrid}>
        <MetricCard
          icon={<Clock className={styles.metricIcon} />}
          label="Total Duration"
          value={totalDuration.toFixed(1)}
          unit="s"
        />
        <MetricCard
          icon={<Activity className={styles.metricIcon} />}
          label="Longest Pause"
          value={maxPause.toFixed(1)}
          unit="s"
        />
        <MetricCard
          icon={<FileText className={styles.metricIcon} />}
          label="Word Count"
          value={wordCount}
        />
      </div>

      <div className={styles.chartSection}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={timelineData}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <defs>
              <linearGradient id="pauseGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={thresholds.color} stopOpacity={0.8} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.8} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="time"
              label={{
                value: 'Time (seconds)',
                position: 'bottom',
                offset: -10,
                style: { fill: '#64748b' }
              }}
              tick={{ fill: '#64748b' }}
            />
            <YAxis
              label={{
                value: 'Pause Duration (seconds)',
                angle: -90,
                position: 'insideLeft',
                offset: 10,
                style: { fill: '#64748b' }
              }}
              tick={{ fill: '#64748b' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={thresholds.word}
              stroke="#10b981"
              strokeDasharray="3 3"
              label={{
                value: "Normal Pause Threshold",
                position: 'right',
                fill: '#10b981'
              }}
            />
            <ReferenceLine
              y={thresholds.sentence}
              stroke="#f59e0b"
              strokeDasharray="3 3"
              label={{
                value: "Significant Pause Threshold",
                position: 'right',
                fill: '#f59e0b'
              }}
            />
            <ReferenceLine
              y={thresholds.emphasis}
              stroke="#ef4444"
              strokeDasharray="3 3"
              label={{
                value: "Dramatic Pause Threshold",
                position: 'right',
                fill: '#ef4444'
              }}
            />
            <Line
              type="monotone"
              dataKey="pause"
              stroke="url(#pauseGradient)"
              strokeWidth={2}
              dot={(props) => {
                if (!props.payload.showDot) return null;
                const colors = {
                  'Normal': '#10b981',
                  'Moderate': '#f59e0b',
                  'Significant': '#ef4444',
                  'Dramatic': '#8b5cf6'
                };
                return (
                  <circle
                    cx={props.cx}
                    cy={props.cy}
                    r={4}
                    fill={colors[props.payload.pauseCategory]}
                    stroke="white"
                    strokeWidth={2}
                  />
                );
              }}
              activeDot={{
                r: 6,
                stroke: thresholds.color,
                strokeWidth: 2,
                fill: 'white'
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={styles.gradientDot}></span>
          <span>Speaking Pattern</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.normalLine}></span>
          <span>Normal Pauses ({thresholds.word.toFixed(1)}s)</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.moderateLine}></span>
          <span>Significant Pauses ({thresholds.sentence.toFixed(1)}s)</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.dramaticLine}></span>
          <span>Dramatic Pauses ({thresholds.emphasis.toFixed(1)}s)</span>
        </div>
      </div>
    </div>
  );
};

export default PauseAnalysis;